// @flow
import { css } from "styled-components";
import GoogleMapReact from "google-map-react";
import { formatPriceWithKOrM, getImage } from "@nested/utils";
import { media } from "@nested/brand";
import { getConfig } from "@nested/config";
import { HousePin } from "@nested/ui/icons";
import { BookIntroCall } from "./BookIntroCall";

const { GOOGLE_API_KEY } = getConfig();

const wrapperStyle = css`
  background-color: ${({ theme }) => theme.palette.hague};
  padding: 40px 20px 20px;

  ${media.desktop`
    padding: 115px 0px;
  `}
`;

const contentWrapper = css`
  position: relative;
  background-color: white;
  border-radius: 10px;
  padding: 40px 25px 20px;
  text-align: center;

  ${media.desktop`
    background-color: transparent;
    color: white;
    text-align: left;
    padding: 0;
    width: 450px;
    flex-grow: 1;
    flex-shrink: 0;
  `}
`;

const valuationHeadingStyle = css`
  color: ${({ theme }) => theme.palette.hague40};
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 1.5px;
  margin-top: 10px;
  text-transform: uppercase;

  ${media.desktop`
    margin-top: 30px;
  `}
`;

const priceRangeStyle = css`
  font-size: 28px;
  line-height: 37px;
  font-weight: 500;

  ${media.desktop`
    font-size: 65px;
    line-height: 80px;
  `}
`;

const imageAndAddressWrapperStyle = css`
  ${media.desktop`
    display: flex;
    flex-direction: row;
    align-items: center;
  `}
`;

const topImageStyle = css`
  position: absolute;
  top: -30px;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 50%;
  border: 4px solid white;
  width: 60px;
  height: 60px;
  overflow: hidden;
  background-color: #ffeef1;
  display: flex;
  align-items: center;
  justify-content: center;

  ${media.desktop`
    display: flex;
    flex-shrink: 0;
    position: static;
    width: 46px;
    height: 46px;
    border: 3px solid white;
    transform: none;
  `}
`;

const addressStyle = css`
  font-size: 14px;
  line-height: 18px;
  max-width: 230px;
  margin: auto;

  ${media.desktop`
    flex-grow: 1;
    max-width: unset;
    margin: unset;
    margin-left: 10px;
    font-size: 18px;
    font-weight: 500;
    line-height: 24px;
  `}
`;

const iconStyle = css`
  width: 52px;
  height: 52px;

  ${media.desktop`
    width: 40px;
    height: 40px;
  `}
`;

const ctaStyle = css`
  display: none;
  ${media.desktop`
    display: block;
    margin-top: 30px;
  `}
`;

const desktopWrapper = css`
  ${media.desktop`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 0 20px;
    max-width: 940px;
    margin: auto;
  `}
`;

const mapWrapperStyle = css`
  display: none;

  ${media.desktop`
    flex-grow: 0;
    flex-shrink: 1;
    display: block;
    width: 100%;
    height: 280px;
    margin-left: 40px;
    overflow: hidden;
    border-radius: 10px;
  `}
`;

const createMapOptions = (maps) => ({
  mapTypeId: maps.MapTypeId.ROADMAP,
  disableDefaultUI: true,
  gestureHandling: "cooperative",
  streetViewControl: false,
  styles: [
    {
      featureType: "road",
      elementType: "geometry",
      stylers: [{ color: "white" }],
    },
    {
      featureType: "road",
      elementType: "labels",
      stylers: [{ visibility: "on" }],
    },
  ],
});

export const formatValuation = (valuation: number) => {
  let i = 0;
  let price = valuation;
  while (price > 1000) {
    price /= 10;
    i += 1;
  }
  const floored_to_3_sig_figs = Math.floor(price) * 10 ** i;
  return formatPriceWithKOrM(floored_to_3_sig_figs);
};

type Props = {
  address: string,
  min: number,
  max: number,
  lat: number,
  lng: number,
};

export const Hero = ({ address, min, max, lat, lng }: Props) => {
  return (
    <section css={wrapperStyle}>
      <div css={desktopWrapper}>
        <div css={contentWrapper}>
          <div css={imageAndAddressWrapperStyle}>
            <div css={topImageStyle}>
              <img
                alt=""
                css={iconStyle}
                src={getImage("illustrations/valuation.png")}
              />
            </div>
            <div css={addressStyle}>{address}</div>
          </div>
          <div css={valuationHeadingStyle}>Your online valuation</div>
          <div css={priceRangeStyle}>
            {formatValuation(min)} - {formatValuation(max)}
          </div>
          <div css={ctaStyle}>
            <BookIntroCall size="large" />
          </div>
        </div>
        <div css={mapWrapperStyle} className="chromatic-ignore">
          <GoogleMapReact
            bootstrapURLKeys={{ key: GOOGLE_API_KEY }}
            defaultCenter={{
              lat,
              lng,
            }}
            defaultZoom={16}
            options={createMapOptions}
          >
            <HousePin
              style={{ marginTop: "-52px", marginLeft: "-26px" }}
              width="52px"
              height="52px"
              lat={lat}
              lng={lng}
              fillStyle={{
                background: "#0A4254",
                colour: "#FF8770",
              }}
            />
          </GoogleMapReact>
        </div>
      </div>
    </section>
  );
};
